/**
 * Dates, ages and weeks — one definition, used by the job, the API, the export and
 * the SPA.
 *
 * ⚠ THE RULE, and everything in this file follows from it: DMP's
 * `technically_executed_at` is `timestamp without time zone` — a wall-clock reading
 * with no offset. It is stored in `snapshot_row` with the same digits, and is only
 * ever read back **in UTC**. Format it in local time anywhere and a 00:30 execution
 * moves to the previous day, changing its age and its ISO week: hard rule 5, one step
 * removed. Every function here that touches such a value uses the UTC accessors, and
 * none of them may be "simplified" to `toLocaleDateString()` without a timezone.
 */
/** DMP's naive `YYYY-MM-DD HH:MM:SS` → the Date we store. Pinned to UTC, see above. */
export function parseDmpTimestamp(raw) {
    const value = new Date(`${raw.trim().replace(" ", "T")}Z`);
    if (Number.isNaN(value.getTime())) {
        throw new Error(`Unparseable DMP timestamp: ${JSON.stringify(raw)}`);
    }
    return value;
}
/** `2026-09-11` from a stored execution timestamp. UTC, always. */
export function isoDateUtc(value) {
    return value.toISOString().slice(0, 10);
}
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** `11 Sep 2026` — en-GB, as the design requires, and computed in UTC. */
export function formatDayMonthYear(value) {
    return `${String(value.getUTCDate()).padStart(2, "0")} ${MONTHS[value.getUTCMonth()]} ${value.getUTCFullYear()}`;
}
/**
 * The business day a moment falls on, in a named timezone, as `YYYY-MM-DD`.
 *
 * Used for `daily_snapshot.runDate`. Derived from the zone rather than from UTC
 * because a job that runs at 06:00 Brussels is 04:00 UTC in summer and 05:00 in
 * winter — taking the UTC date would be right by luck for most of the year.
 */
export function businessDate(now, timeZone) {
    const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(now);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? "";
    return `${get("year")}-${get("month")}-${get("day")}`;
}
/** `YYYY-MM-DD` → the UTC-midnight Date that a Postgres `date` column round-trips. */
export function dateOnly(iso) {
    return new Date(`${iso}T00:00:00.000Z`);
}
/**
 * Whole days between an execution and the day being reported on.
 *
 * ⚠ `asOf` is the SNAPSHOT'S day, never `new Date()`. An age recomputed at read time
 * would tick over at midnight while the snapshot it describes is a morning fact — and
 * two people opening the same snapshot either side of midnight would read different
 * ages for the same row. Same number for everybody, for that day.
 */
export function ageInDays(executedAt, asOf) {
    const from = Date.UTC(executedAt.getUTCFullYear(), executedAt.getUTCMonth(), executedAt.getUTCDate());
    const to = Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth(), asOf.getUTCDate());
    return Math.max(0, Math.round((to - from) / 86_400_000));
}
/**
 * The age buckets from screen 04, in order, with the tone each is drawn in.
 *
 * `max` is inclusive; the last bucket is open-ended. Exported as data rather than as a
 * chain of ifs so the chart, the filter chips and the export all enumerate the same six.
 */
export const AGE_BUCKETS = [
    { label: "0–7 days", min: 0, max: 7, tone: "emerald" },
    { label: "8–30 days", min: 8, max: 30, tone: "blue" },
    { label: "31–90 days", min: 31, max: 90, tone: "amber" },
    { label: "91–180 days", min: 91, max: 180, tone: "orange" },
    { label: "181–365 days", min: 181, max: 365, tone: "orange" },
    { label: "Over a year", min: 366, max: Number.MAX_SAFE_INTEGER, tone: "red" },
];
export function ageBucketOf(days) {
    for (const bucket of AGE_BUCKETS) {
        if (days >= bucket.min && days <= bucket.max)
            return bucket.label;
    }
    return AGE_BUCKETS[AGE_BUCKETS.length - 1].label;
}
/** The pill colour for an age. Mirrors `CS.ageTone` in the validated mockups exactly. */
export function ageTone(days) {
    if (days > 365)
        return "red";
    if (days > 90)
        return "orange";
    if (days > 30)
        return "amber";
    return "slate";
}
/**
 * ISO-8601 week of a stored execution timestamp, in UTC.
 *
 * ISO weeks start on Monday and belong to the year containing their Thursday, which is
 * why this is not `Math.floor(dayOfYear / 7)`. The report is read week by week — Ubi
 * asked for *"how many orders delivered per week"* — and a week that is off by one
 * moves volume between the bars people argue about.
 */
export function isoWeek(value) {
    const thursday = new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
    // Shift to the Thursday of this ISO week (Monday = 0 … Sunday = 6).
    thursday.setUTCDate(thursday.getUTCDate() - ((thursday.getUTCDay() + 6) % 7) + 3);
    const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
    const week = 1 +
        Math.round(((thursday.getTime() - firstThursday.getTime()) / 86_400_000 -
            3 +
            ((firstThursday.getUTCDay() + 6) % 7)) /
            7);
    return { year: thursday.getUTCFullYear(), week, label: `W${String(week).padStart(2, "0")}` };
}
/** Sortable ISO week key, `2026-W38`. Needed because W01 of next year follows W52. */
export function isoWeekKey(value) {
    const { year, week } = isoWeek(value);
    return `${year}-W${String(week).padStart(2, "0")}`;
}
/**
 * `2 583` — a space every three digits, as `CS.fmt` does in the mockups.
 *
 * A space rather than a comma because the audience is Belgian and that is what Ubi
 * validated. U+00A0 rather than a plain space so a headline figure can never wrap
 * across two lines in a narrow KPI card; it renders at the same width.
 */
export function formatNumber(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}
/**
 * The wall clock a UTC instant shows in a named zone, as numbers.
 *
 * The building block for scheduling in local time. `Intl` is the only thing in the
 * platform that knows when Brussels last changed offset, so the conversion is done by
 * asking it rather than by adding an hour and hoping.
 */
function wallClockInZone(instant, timeZone) {
    const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone,
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    }).formatToParts(instant);
    const num = (type) => Number(parts.find((p) => p.type === type)?.value ?? "0");
    // Intl renders midnight as hour 24 in some ICU versions; normalise it to 0.
    const hour = num("hour") % 24;
    return { year: num("year"), month: num("month"), day: num("day"), hour, minute: num("minute") };
}
/**
 * The UTC instant at which a given local date and time occurs in a named zone.
 *
 * Solved by correction rather than from an offset table: guess that the local time is
 * UTC, ask the zone what that instant actually reads as, and shift by the difference.
 * One correction settles every ordinary case; the loop exists for the two mornings a
 * year when the correction itself crosses the transition.
 *
 * The two awkward days, both decided deliberately rather than left to the loop:
 *
 *   · **Spring forward** — 02:30 does not exist on that Sunday. The corrections then
 *     oscillate between the instants either side of the gap, and this returns the LATER
 *     one, so "run at 02:30" becomes 03:30 rather than 01:30. A job set inside the gap
 *     runs after it, never before it, and never fails to run at all.
 *   · **Autumn back** — 02:30 happens twice. This returns the second, post-transition
 *     one. Either is defensible; what matters is that it is always the same one.
 */
export function zonedTimeToUtc(isoDate, hour, minute, timeZone) {
    const targetMinutes = hour * 60 + minute;
    /** How far `guess` is from the wanted local time, in minutes. Zero means solved. */
    const driftOf = (guess) => {
        const shown = wallClockInZone(guess, timeZone);
        const shownDate = `${shown.year}-${String(shown.month).padStart(2, "0")}-${String(shown.day).padStart(2, "0")}`;
        const dayDrift = (Date.parse(`${isoDate}T00:00:00Z`) - Date.parse(`${shownDate}T00:00:00Z`)) / 60_000;
        return dayDrift + (targetMinutes - (shown.hour * 60 + shown.minute));
    };
    const shift = (from, minutes) => new Date(from.getTime() + minutes * 60_000);
    let guess = new Date(`${isoDate}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00Z`);
    let drift = driftOf(guess);
    if (drift === 0)
        return guess;
    // The first correction is unconditional: the initial guess is off by the whole
    // offset, so its drift says nothing about whether the local time exists.
    guess = shift(guess, drift);
    drift = driftOf(guess);
    for (let pass = 0; pass < 3 && drift !== 0; pass++) {
        const next = shift(guess, drift);
        const nextDrift = driftOf(next);
        if (nextDrift !== 0 && Math.sign(nextDrift) !== Math.sign(drift)) {
            // Correcting one way overshoots and correcting back undershoots: the local time
            // falls in the spring-forward gap. Resolve forward.
            return new Date(Math.max(guess.getTime(), next.getTime()));
        }
        guess = next;
        drift = nextDrift;
    }
    return guess;
}
/**
 * The next time `hour:minute` comes round in a named zone, strictly after `after`.
 *
 * Re-derived on every run rather than by adding 24 hours to the last one, so the
 * snapshot stays at 06:00 Brussels across both DST changes instead of drifting to
 * 05:00 or 07:00 for half the year — and so a missed run does not shift the schedule
 * permanently.
 */
export function nextOccurrence(after, hour, minute, timeZone) {
    const today = businessDate(after, timeZone);
    const candidate = zonedTimeToUtc(today, hour, minute, timeZone);
    if (candidate.getTime() > after.getTime())
        return candidate;
    // ⚠ Tomorrow by CALENDAR arithmetic, never by adding hours to the candidate. It used
    // to add 36 hours, which lands on the day after tomorrow for any hour from 12:00 on —
    // a 23:00 job would have run every other day.
    return zonedTimeToUtc(addCalendarDays(today, 1), hour, minute, timeZone);
}
/** The most recent time `hour:minute` came round in a named zone, at or before `atOrBefore`. */
export function previousOccurrence(atOrBefore, hour, minute, timeZone) {
    const today = businessDate(atOrBefore, timeZone);
    const candidate = zonedTimeToUtc(today, hour, minute, timeZone);
    if (candidate.getTime() <= atOrBefore.getTime())
        return candidate;
    return zonedTimeToUtc(addCalendarDays(today, -1), hour, minute, timeZone);
}
/**
 * How many mornings a snapshot is behind: 0 when it is the newest one that should exist.
 *
 * ⚠ Measured against the last scheduled run time that has PASSED, not against today's
 * date. Between midnight and 06:00 yesterday's snapshot is still the current one, and
 * calling it a day old would put a warning on every screen every night. After 06:00 a
 * snapshot from yesterday means this morning's run did not happen.
 */
export function daysBehindSchedule(runDate, now, hour, minute, timeZone) {
    const due = businessDate(previousOccurrence(now, hour, minute, timeZone), timeZone);
    return Math.max(0, Math.round((Date.parse(`${due}T00:00:00Z`) - Date.parse(`${runDate}T00:00:00Z`)) / 86_400_000));
}
/** `YYYY-MM-DD` plus whole calendar days. Dates have no timezone, so neither does this. */
function addCalendarDays(isoDate, days) {
    return isoDateUtc(new Date(Date.parse(`${isoDate}T00:00:00Z`) + days * 86_400_000));
}
//# sourceMappingURL=time.js.map